import { useEffect, useRef, useState } from 'react';

import { forceSyncCapabilities } from '#lib/authToken';
import { queryClient } from '#lib/queryClient';
import { useErrorStore } from '#lib/stores/errorStore';
import { ensureOrgExists } from '#orgFeature/api/orgService';

/**
 * Login self-heal: if a confirmed user has no organization (the org-provisioning step failed
 * at signup), this calls the idempotent ensure-org-exists endpoint, refreshes the JWT so the new
 * org_id/capabilities claims land, and refetches org info. Runs at most once per user per
 * session to avoid loops; on failure it surfaces a toast instead of retrying forever.
 *
 * @param {object} params
 * @param {object|null} params.session - Better Auth session (or null when logged out)
 * @param {object|undefined} params.orgInfo - Resolved org info, or undefined when missing/errored
 * @param {boolean} params.orgInfoLoading - Whether the org info query is still in flight
 * @param {boolean} params.orgInfoIsError - Whether the org info query settled in error
 * @param {object|null} params.orgInfoError - The org info query error (has `status` on HTTP errors)
 * @param {Function} params.refetchOrgInfo - Refetches the org info query
 * @param {'pending'|'error'|'success'} params.pendingInvitesStatus - Status of the user's pending-invites query
 * @param {Array|undefined} params.pendingInvites - The user's open org invites
 * @returns {{ healing: boolean }} Whether a heal attempt is currently in flight
 */
export function useEnsureOrg({
	session,
	orgInfo,
	orgInfoLoading,
	orgInfoIsError,
	orgInfoError,
	refetchOrgInfo,
	pendingInvitesStatus,
	pendingInvites,
}) {
	const healedForUserRef = useRef(null);
	const [healing, setHealing] = useState(false);
	const showError = useErrorStore((state) => state.showError);

	const userId = session?.user?.id;
	const isConfirmed = Boolean(session?.user?.emailVerified);

	// Reset the once-per-user guard on logout / user switch.
	useEffect(() => {
		if (!userId) healedForUserRef.current = null;
	}, [userId]);

	useEffect(() => {
		// "Genuinely missing" = the org query has settled (not loading) with no org. A 403 means
		// the user has no membership; we only heal on that, not on transient 500s/network errors.
		const orgGenuinelyMissing =
			Boolean(userId) &&
			isConfirmed &&
			!orgInfoLoading &&
			(orgInfoIsError ? orgInfoError?.status === 403 : !orgInfo);

		if (!orgGenuinelyMissing) return;
		if (healedForUserRef.current === userId) return;
		// An invitee without an org must see the invite modal first; an own org would hide it.
		if (pendingInvitesStatus === 'pending' || pendingInvites?.length > 0) return;

		// Mark before awaiting so re-renders during the heal don't re-trigger it.
		healedForUserRef.current = userId;

		let cancelled = false;
		setHealing(true);

		(async () => {
			try {
				await ensureOrgExists();
				await forceSyncCapabilities();
				await queryClient.invalidateQueries();
				await refetchOrgInfo();
			} catch (error) {
				if (!cancelled) showError({ error, title: 'Konto konnte nicht eingerichtet werden' });
				// Keep the guard set — surfacing the error once is better than an infinite retry loop.
			} finally {
				// Always clear the healing flag, even if this effect re-ran mid-heal (forceSync
				// triggers a new session reference). Tying this to `cancelled` would leave the
				// AuthProvider stuck on its fullscreen loader forever. The once-per-user ref guard
				// already prevents a second concurrent heal, so there is no competing writer.
				setHealing(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		userId,
		isConfirmed,
		orgInfo,
		orgInfoLoading,
		orgInfoIsError,
		orgInfoError,
		refetchOrgInfo,
		pendingInvitesStatus,
		pendingInvites,
		showError,
	]);

	return { healing };
}
